import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const UPP_AMBER = '#D97706';

/* ─── Shared sub-components ────────────────────────────────────── */
const UpsellTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/upsell-plans-pro"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Upsell Plans Pro
      </Link>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: UPP_AMBER }}>
          <ShieldCheck size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Upsell Plans Pro</span>
      </div>
      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const UpsellFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Upsell Plans Pro · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/upsell-plans-pro/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/upsell-plans-pro/privacy" className="font-mono text-[0.55rem] tracking-widest uppercase" style={{ color: UPP_AMBER }}>Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, id, children }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest uppercase shrink-0 mt-1.5" style={{ color: UPP_AMBER }}>{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

const SubHeading = ({ children }) => (
  <h3 className="font-mono text-[0.65rem] tracking-widest text-[#374151] uppercase mb-2 mt-5">{children}</h3>
);

const DataPoint = ({ children }) => (
  <li className="text-[#374151] text-sm leading-relaxed flex items-start gap-2.5">
    <span className="w-1 h-1 rounded-full shrink-0 mt-2" style={{ backgroundColor: UPP_AMBER }} />
    <span>{children}</span>
  </li>
);

const B = ({ children }) => <strong className="font-medium text-[#0F172A]">{children}</strong>;

const Mail = () => (
  <a href="mailto:hello@smartsolution.ai" style={{ color: UPP_AMBER }} className="hover:underline">hello@smartsolution.ai</a>
);

/* ─── Page ──────────────────────────────────────────────────────── */
export const UpsellPlansProPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Upsell Plans Pro Privacy Policy"
        description="Privacy policy for the Upsell Plans Pro Shopify app by Smart Solutions."
        path="/apps/upsell-plans-pro/privacy"
      />
      <UpsellTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Privacy Policy
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: October 14, 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Quick jump */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 bg-white border border-[#E2E8F0] p-6"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
          >
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase mb-3">Jump to section</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {[
                ['#intro', 'Who We Are'],
                ['#data-collected', 'Data We Collect'],
                ['#data-use', 'How We Use Data'],
                ['#legal-basis', 'Legal Basis'],
                ['#data-sharing', 'Data Sharing'],
                ['#shopify-api', 'Shopify API Use'],
                ['#retention', 'Retention'],
                ['#security', 'Security'],
                ['#rights', 'Your Rights'],
                ['#cookies', 'Cookies'],
                ['#contact', 'Contact'],
              ].map(([href, label]) => (
                <a key={href} href={href} className="text-xs hover:underline" style={{ color: UPP_AMBER }}>{label}</a>
              ))}
            </div>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Introduction & Who We Are" id="intro">
            <P>
              This Privacy Policy explains how Smart Solutions ("we", "us", "our") collects, uses, stores, and
              protects personal data when merchants and their customers use Upsell Plans Pro, our Shopify app for
              selling protection plans, services, and product add-ons.
            </P>
            <P>
              Merchants who install the App are the data controllers for the personal data of their customers and
              installers. We process that data on the merchant's behalf, solely to provide the App. We are committed
              to handling all personal data responsibly and in compliance with applicable data protection laws,
              including the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA)
              where applicable.
            </P>
            <P>
              For any privacy-related questions, contact us at: <Mail />
            </P>
          </Section>

          <Section num="02" title="What Data We Collect" id="data-collected">
            <SubHeading>Merchant Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Shop domain, shop name, shop email address, and primary domain</DataPoint>
              <DataPoint>Support and alert email addresses entered in the App's settings</DataPoint>
              <DataPoint>App settings, widget design, and offer configuration (plans, services, add-ons, pricing rules)</DataPoint>
              <DataPoint>Shopify access tokens issued when the App is installed</DataPoint>
            </ul>

            <SubHeading>Customer Data (collected on the merchant's behalf)</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Name, email address, and Shopify customer ID</DataPoint>
              <DataPoint>Order number and order ID</DataPoint>
              <DataPoint>Product purchased, price, and serial number</DataPoint>
              <DataPoint>For installation and other services: phone number, service address, and postal code</DataPoint>
              <DataPoint>Preferred and confirmed appointment dates, and booking notes</DataPoint>
              <DataPoint>Claim descriptions, claim photos, and messages exchanged with the merchant</DataPoint>
            </ul>

            <SubHeading>Installer & Staff Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>The merchant's installer directory — names, email addresses, and phone numbers entered by the merchant</DataPoint>
              <DataPoint>Standard Shopify session details for staff who open the App in the Shopify admin</DataPoint>
            </ul>
          </Section>

          <Section num="03" title="How We Use Your Data" id="data-use">
            <P>We use the data we collect solely to provide and operate Upsell Plans Pro. Specifically:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint>To display protection plans, services, and add-ons on the merchant's storefront</DataPoint>
              <DataPoint>To register every plan sold, issue its reference number, and track its coverage period</DataPoint>
              <DataPoint>To let customers look up coverage, file claims, upload photos, and message the merchant</DataPoint>
              <DataPoint>To schedule installations and assign them to the merchant's installers</DataPoint>
              <DataPoint>To send automatic emails about plans, claims, and appointments on the merchant's behalf</DataPoint>
              <DataPoint>To fulfil plan lines, create replacement orders for approved claims, and cancel plans on refund</DataPoint>
              <DataPoint>To show the merchant analytics such as revenue, attach rate, and loss ratio</DataPoint>
              <DataPoint>To calculate plan usage and billing through Shopify</DataPoint>
            </ul>
            <P>
              We do not use merchant or customer data for advertising, profiling, or any purpose outside of providing
              the App's functionality.
            </P>
          </Section>

          <Section num="04" title="Legal Basis for Processing" id="legal-basis">
            <P>
              We process personal data under the following legal bases (where GDPR applies):
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><B>Contract:</B> Processing is necessary to deliver the App services the merchant has subscribed to via the Shopify App Store, and to deliver the plans and services customers have purchased from the merchant.</DataPoint>
              <DataPoint><B>Legitimate Interests:</B> Keeping plan, claim, and installation records is in the legitimate interest of the merchant and its customers for honoring coverage and resolving claims.</DataPoint>
              <DataPoint><B>Processor role:</B> For customer and installer data, the merchant is the data controller and determines the legal basis; we act as a data processor on the merchant's instructions.</DataPoint>
            </ul>
          </Section>

          <Section num="05" title="Data Sharing — Third Parties" id="data-sharing">
            <P>
              We do not sell, rent, or trade merchant or customer data to any third party.
            </P>
            <P>
              Data is shared only with the following sub-processors, solely to operate the App:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><B>Shopify:</B> The App integrates with Shopify to authenticate the merchant, read orders, and manage plan products and fulfilments. Shopify's use of data is governed by Shopify's own privacy policies.</DataPoint>
              <DataPoint><B>Shopify Flow:</B> On the Pro plan, the App sends plan, claim, and installation details to the merchant's own Shopify Flow workflows. What happens next is controlled by the merchant.</DataPoint>
              <DataPoint><B>Microsoft Azure:</B> Our application is hosted on Microsoft Azure App Service.</DataPoint>
              <DataPoint><B>Database hosting:</B> Plan, claim, installation, and settings records are stored in a managed PostgreSQL database.</DataPoint>
              <DataPoint><B>Transactional email provider:</B> Used to deliver the App's automatic emails. It receives the recipient's email address and the message content.</DataPoint>
            </ul>
            <P>
              No data is shared with advertisers, analytics providers, or AI services.
            </P>
          </Section>

          <Section num="06" title="Shopify API Data Use" id="shopify-api">
            <P>
              The App requests only the Shopify access scopes it needs to function:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><B>read_orders:</B> to detect plans and services purchased, register them, and cancel plans when an order is refunded</DataPoint>
              <DataPoint><B>write_products, read_publications, write_publications:</B> to create and maintain the unlisted products that represent plans and services, and control which sales channels they appear on</DataPoint>
              <DataPoint><B>read_themes:</B> to check that the App's widget is enabled in the merchant's theme</DataPoint>
              <DataPoint><B>write_draft_orders:</B> to create free replacement orders for approved claims</DataPoint>
              <DataPoint><B>write_merchant_managed_fulfillment_orders:</B> to automatically fulfil plan and service lines</DataPoint>
            </ul>
            <P>
              All Shopify API data is used exclusively to provide the App's functionality and is handled in
              accordance with Shopify's API Terms of Service and Partner Program Agreement.
            </P>
          </Section>

          <Section num="07" title="Data Retention" id="retention">
            <P>
              Data is retained for as long as the App is installed on the merchant's Shopify store, so that plans can
              be honored for their full coverage period.
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><B>On uninstall:</B> sessions and pending background jobs are deleted immediately.</DataPoint>
              <DataPoint><B>Store data:</B> all of the store's data is permanently deleted when Shopify sends the shop redaction request, approximately 48 hours after uninstall.</DataPoint>
              <DataPoint><B>Customer redaction:</B> when Shopify sends a customer redaction request, that customer's personal fields are cleared and their claim photos deleted.</DataPoint>
              <DataPoint><B>Unused uploads:</B> photos uploaded but never attached to a claim are deleted after 24 hours.</DataPoint>
            </ul>
            <P>
              Customer data requests received from Shopify appear on the App's "Customer data requests" page, where the
              merchant can export the requested data. Merchants may also request deletion of their store's data at any
              time by contacting us at <Mail />. We will process verified requests within 30 days.
            </P>
          </Section>

          <Section num="08" title="Security" id="security">
            <P>
              We take the security of your data seriously. Our security measures include:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>All data is transmitted over HTTPS/TLS</DataPoint>
              <DataPoint>Shopify access tokens are stored server-side and never exposed to the storefront or to customers</DataPoint>
              <DataPoint>Our hosting infrastructure is protected by cloud-level security controls, access policies, and monitoring</DataPoint>
              <DataPoint>Unused photo uploads are removed automatically after 24 hours</DataPoint>
            </ul>
            <P>
              While we take all reasonable precautions, no method of transmission or storage is 100% secure.
              If you believe your account has been compromised, contact us immediately.
            </P>
          </Section>

          <Section num="09" title="Your Rights" id="rights">
            <P>
              Depending on your location, you may have the following rights regarding your personal data:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><B>Access:</B> Request a copy of the personal data we hold about you.</DataPoint>
              <DataPoint><B>Rectification:</B> Request that inaccurate data be corrected.</DataPoint>
              <DataPoint><B>Erasure:</B> Request deletion of your personal data ("right to be forgotten").</DataPoint>
              <DataPoint><B>Portability:</B> Request your data in a machine-readable format.</DataPoint>
              <DataPoint><B>Objection:</B> Object to processing based on legitimate interests.</DataPoint>
            </ul>
            <P>
              Note: The merchant is the data controller for its customers' and installers' personal data stored in
              the App. Customers should contact the store they purchased from in the first instance; requests made
              through Shopify are passed to us and handled as described in Data Retention. Merchants may contact us
              at <Mail /> to exercise rights on behalf of their store.
            </P>
          </Section>

          <Section num="10" title="Cookies & Browser Storage" id="cookies">
            <P>
              The App does not set cookies on the storefront and does not use tracking or advertising technologies.
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>The storefront widget stores a single value in the shopper's browser (localStorage key <code className="font-mono text-xs">upsell_zip</code>) to remember the postal code entered for service booking</DataPoint>
              <DataPoint>This value is used only to pre-fill the booking form and can be cleared at any time by clearing browser data</DataPoint>
              <DataPoint>The embedded admin app uses Shopify's standard session authentication</DataPoint>
            </ul>
          </Section>

          <Section num="11" title="Children's Privacy">
            <P>
              Upsell Plans Pro is a business-facing application intended for use by Shopify merchants. The App is not
              directed at individuals under the age of 13, and we do not knowingly collect personal data from children.
            </P>
          </Section>

          <Section num="12" title="Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time to reflect changes in our practices, legal
              requirements, or service features. We will notify merchants of material changes via the Shopify
              admin or by email. The "last updated" date at the top of this page will always reflect the most
              recent revision.
            </P>
            <P>
              Continued use of the App after an updated Privacy Policy is posted constitutes your acceptance
              of the changes.
            </P>
          </Section>

          <Section num="13" title="Contact Us" id="contact">
            <P>
              For any privacy-related questions, requests, or concerns, please contact us:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <Mail />
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              Last updated: October 14, 2026
            </p>
            <Link
              to="/apps/upsell-plans-pro/terms"
              className="font-mono text-[0.6rem] tracking-widest hover:underline uppercase"
              style={{ color: UPP_AMBER }}
            >
              View Terms &amp; Conditions →
            </Link>
          </div>
        </div>
      </div>

      <UpsellFooter />
    </div>
  );
};
